import { useState, useEffect, useRef } from "react";
import { Info } from "lucide-react";

// Discreet inline information affordance.
// - 10px icon, soft grey — invisible until you look for it
// - Hover OR focus OR tap opens the tip (works on touch)
// - Auto-closes on outside click / Esc
// - Side: "top" | "bottom" | "left" | "right"
//
// Usage:
//   <InfoTip side="bottom">Sum of monthly fees for every confirmed project.</InfoTip>

const SIDES = {
  top:    "bottom-full mb-2 left-1/2 -translate-x-1/2",
  bottom: "top-full mt-2 left-1/2 -translate-x-1/2",
  left:   "right-full mr-2 top-1/2 -translate-y-1/2",
  right:  "left-full ml-2 top-1/2 -translate-y-1/2",
};

export default function InfoTip({ children, side = "bottom", label, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span ref={ref} className="relative inline-flex items-center align-middle ml-1.5" data-testid={testid}>
      <button
        type="button"
        aria-label={label || "More information"}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="text-[var(--soft-fg)] hover:text-[var(--ink)] focus:outline-none focus:text-[var(--ink)] cursor-help opacity-60 hover:opacity-100 transition-opacity"
      >
        <Info size={10} strokeWidth={1.75} />
      </button>
      {open && (
        <span
          role="tooltip"
          className={`absolute z-50 ${SIDES[side] || SIDES.bottom} w-64 p-3 bg-white border border-[var(--rule-strong)] shadow-sm text-[11px] font-mono-num text-[var(--ink)] leading-relaxed normal-case tracking-normal whitespace-normal text-left`}
        >
          {children}
        </span>
      )}
    </span>
  );
}
